import {
  CloudUpload as UploadCloudIcon,
  Delete as XIcon,
  Refresh as RefreshCwIcon,
  AddPhotoAlternate as AddPhotoIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
  Theme,
  alpha,
  Tooltip,
} from "@mui/material";
import { motion, AnimatePresence, MotionProps } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

interface ImageDropZoneProps {
  onDropFile: (acceptedFile: File) => void;
  initialImage?: string | null;
  isMinified?: boolean; // New prop for compact display
  height?: number | string; // Optional height prop
  width?: number | string; // Optional width prop
}

const MotionImg = motion.img as React.ComponentType<
  React.ImgHTMLAttributes<HTMLImageElement> & MotionProps
>;

export const ImageDropzone: React.FC<ImageDropZoneProps> = ({
  onDropFile,
  initialImage = null,
  isMinified = false,
  height = isMinified ? 60 : 256,
  width = "100%",
}) => {
  const theme = useTheme();
  const [image, setImage] = useState<string | null>(initialImage);
  const [isLoading, setIsLoading] = useState(false);

  // Update image state if initialImage prop changes
  useEffect(() => {
    setImage(initialImage);
  }, [initialImage]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // Check if we have any files before trying to access them
      if (!acceptedFiles || acceptedFiles.length === 0) {
        console.warn("No files were accepted in the drop");
        return;
      }

      const file = acceptedFiles[0];

      // Make sure file exists and is an image before proceeding
      if (file && file.type && file.type.startsWith("image/")) {
        setIsLoading(true);

        // Create a new FileReader
        const reader = new FileReader();

        // Set up the onload event handler
        reader.onload = () => {
          if (reader.result) {
            setImage(reader.result as string);
          }
          setIsLoading(false);
        };

        // Set up error handling
        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          setIsLoading(false);
        };

        // Start reading the file
        try {
          reader.readAsDataURL(file);
        } catch (error) {
          console.error("Error initiating file read:", error);
          setIsLoading(false);
          return;
        }

        // Call the onDropFile callback
        try {
          onDropFile(file);
        } catch (error) {
          console.error("Error in onDropFile callback:", error);
        }
      } else {
        console.warn("Dropped file is not an image or is invalid");
      }
    },
    [onDropFile]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
    maxFiles: 1,
    noClick: image !== null && !isMinified, // Disable click when we have an image already (except in minified mode)
  });

  const removeImage = () => {
    setImage(null);
  };

  const getDropzoneStyles = (theme: Theme) => ({
    border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: isMinified ? theme.spacing(1) : theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    height: height,
    width: width,
    backgroundColor: isDragActive
      ? alpha(theme.palette.primary.light, 0.1)
      : theme.palette.background.default,
    transition: theme.transitions.create(["border-color", "background-color"]),
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
    cursor: "pointer",
  });

  // Minified dropzone content
  const minifiedDropzoneContent = (
    <Tooltip title="Upload image">
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <AddPhotoIcon
          color={isDragActive ? "primary" : "disabled"}
          sx={{ fontSize: isMinified ? 28 : 48 }}
        />
      </Box>
    </Tooltip>
  );

  // Standard dropzone content
  const standardDropzoneContent = (
    <motion.div
      animate={{
        scale: isDragActive ? 1.05 : 1,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20,
      }}
    >
      <Box mb={1} display="flex" justifyContent="center">
        <motion.div
          animate={{
            rotate: isDragActive ? 360 : 0,
          }}
          transition={{
            duration: 0.5,
          }}
        >
          <UploadCloudIcon color={isDragActive ? "primary" : "disabled"} sx={{ fontSize: 48 }} />
        </motion.div>
      </Box>
      <Typography variant="h6" color="textPrimary" mb={1}>
        {isDragActive ? "Drop the image here" : "Drag & drop an image"}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        or click to browse
      </Typography>
    </motion.div>
  );

  // Minified image preview
  const minifiedImagePreview = (
    <Box
      position="relative"
      borderRadius={1}
      overflow="hidden"
      boxShadow={1}
      height={height}
      width={width}
    >
      <MotionImg
        src={image}
        alt="Uploaded"
        style={{
          width: "100%",
          height: height,
          objectFit: "cover",
        }}
        initial={{ filter: "blur(10px)" }}
        animate={{ filter: "blur(0px)" }}
        transition={{ duration: 0.5 }}
      />

      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        sx={{
          background: "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.3))",
          opacity: 0,
          transition: "opacity 0.2s ease",
          "&:hover": {
            opacity: 1,
          },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={open}
          sx={{
            backgroundColor: alpha(theme.palette.common.white, 0.2),
            backdropFilter: "blur(4px)",
            p: 0.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.3),
            },
          }}
        >
          <RefreshCwIcon sx={{ fontSize: 16, color: "white" }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={removeImage}
          sx={{
            backgroundColor: alpha(theme.palette.common.white, 0.2),
            backdropFilter: "blur(4px)",
            p: 0.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.3),
            },
          }}
        >
          <XIcon sx={{ fontSize: 16, color: "white" }} />
        </IconButton>
      </Box>
    </Box>
  );

  // Standard image preview
  const standardImagePreview = (
    <Box
      position="relative"
      borderRadius={2}
      overflow="hidden"
      boxShadow={3}
      sx={{
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxHeight: "1024px",
          maxWidth: "1024px",
          display: "flex",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <MotionImg
          src={image}
          alt="Uploaded Image"
          style={{
            maxWidth: "100%",
            maxHeight: "1024px",
            objectFit: "contain",
            backgroundColor: "#f5f5f5", // Light background to see image boundaries
          }}
          initial={{ filter: "blur(10px)" }}
          animate={{ filter: "blur(0px)" }}
          transition={{ duration: 0.5 }}
        />
      </Box>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        sx={{
          background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
          padding: theme.spacing(2),
        }}
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
        borderRadius="0!important"
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-end">
          <Box>
            <Typography variant="h6" color="white">
              {initialImage && image === initialImage ? "Current Image" : "Image uploaded"}
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.8)">
              Looking great!
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={open}
              sx={{
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                backdropFilter: "blur(4px)",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.3),
                },
              }}
              aria-label="Change image"
            >
              <RefreshCwIcon sx={{ color: "white" }} />
            </IconButton>
            <IconButton
              onClick={removeImage}
              sx={{
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                backdropFilter: "blur(4px)",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.3),
                },
              }}
              aria-label="Remove image"
            >
              <XIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Minified loading state
  const minifiedLoadingState = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: height,
        width: width,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.default",
      }}
    >
      <CircularProgress size={24} />
    </Box>
  );

  // Standard loading state
  const standardLoadingState = (
    <Box
      border={1}
      borderColor="divider"
      borderRadius={2}
      p={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height={height}
      bgcolor="background.default"
    >
      <Box mb={2}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          <CircularProgress color="primary" />
        </motion.div>
      </Box>
      <Typography variant="h6" color="textPrimary">
        Processing image...
      </Typography>
    </Box>
  );

  return (
    <Box width={width}>
      <AnimatePresence mode="wait">
        {!image ? (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, y: isMinified ? 5 : 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: isMinified ? -5 : -10 }}
            transition={{ duration: isMinified ? 0.2 : 0.3 }}
          >
            <Box {...getRootProps()} sx={getDropzoneStyles(theme)} tabIndex={0}>
              <input {...getInputProps()} aria-label="Image upload" />
              {isMinified ? minifiedDropzoneContent : standardDropzoneContent}
            </Box>
          </motion.div>
        ) : isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {isMinified ? minifiedLoadingState : standardLoadingState}
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: isMinified ? 0.98 : 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: isMinified ? 0.98 : 0.95 }}
            transition={{
              duration: isMinified ? 0.3 : 0.4,
              type: "spring",
            }}
          >
            {isMinified ? minifiedImagePreview : standardImagePreview}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
